import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import { NAIRA } from "../../defaults/utilities";
import { formatCurrency } from "../../helpers/currency";
import dayjs from "dayjs";
import {useGetAllBudgetItemsUnpaginated} from "../../hooks/budgetItem/budgetItemQuery";
import {useCreateBudgetFlow, useDeleteBudgetFlow, useGetAllFlows, useGetFlowsByAccount, useUpdateBudgetFlow} from "../../hooks/budget/flowsQuery";

const { Title } = Typography;
const { Option } = Select;

const dateFormat = 'YYYY-MM-DD';

export const InflowOutflowManager = ({ budgetItem, onUpdate }) => {
  const [flows, setFlows] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({});
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();

  const createFlow = useCreateBudgetFlow();
  const updateFlow = useUpdateBudgetFlow();
  const deleteFlow = useDeleteBudgetFlow();
  const getFlowsByAccount = useGetFlowsByAccount();
  const getAllFlows = useGetAllFlows();
  const getSubAccounts = useGetAllBudgetItemsUnpaginated();

  useEffect(() => {
    loadSubAccounts();
  }, []);

  useEffect(() => {
    loadFlows(1, 10, filters);
  }, [budgetItem, filters]);

  const loadSubAccounts = async () => {
    try {
      const data = await getSubAccounts();
      setSubAccounts(data || []);
    } catch (error) {
      message.error("Failed to load sub-accounts");
    }
  };

  const loadFlows = async (page = 1, pageSize = 10, appliedFilters = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: page - 1,
        size: pageSize,
        ...appliedFilters
      };

      // If budgetItem has subAccount, add it to filters
      if (budgetItem?.subAccount?.id && !appliedFilters.sourceAccountId && !appliedFilters.destinationAccountId) {
        queryParams.sourceAccountId = budgetItem.subAccount.id;
      }

      const response = await getAllFlows(queryParams);

      if (response?.content) {
        setFlows(response.content);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.totalElements || 0
        });
      } else {
        // Fallback to old method if new endpoint fails
        const data = await getFlowsByAccount(budgetItem?.subAccount?.id || 0);
        setFlows(data || []);
        setPagination({ current: 1, pageSize: 10, total: (data || []).length });
      }
    } catch (error) {
      message.error("Failed to load flows");
      setFlows([]);
      setPagination({ current: 1, pageSize: 10, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        sourceAccountId: values.sourceAccountId,
        destinationAccountId: values.destinationAccountId,
        amount: parseFloat(values.amount),
        date: values.date.format(dateFormat),
        flowType: values.flowType,
        identifier: values.identifier || null,
        description: values.description || null
      };

      if (editingFlow) {
        payload.id = editingFlow.id;
        await updateFlow(payload);
        message.success("Flow updated successfully");
      } else {
        await createFlow(payload);
        message.success("Flow created successfully");
      }

      setShowModal(false);
      setEditingFlow(null);
      form.resetFields();
      await loadFlows(pagination.current, pagination.pageSize, filters);
      onUpdate && onUpdate();
    } catch (error) {
      console.error(error);
      message.error(editingFlow ? "Failed to update flow" : "Failed to create flow");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (flow) => {
    setEditingFlow(flow);
    form.setFieldsValue({
      ...flow,
      date: dayjs(flow.date),
      sourceAccountId: flow.sourceAccount?.id,
      destinationAccountId: flow.destinationAccount?.id
    });
    setShowModal(true);
  };

  const handleDelete = async (flow) => {
    setLoading(true);
    try {
      await deleteFlow(flow.id);
      message.success("Flow deleted successfully");
      //await loadFlows(pagination.current, pagination.pageSize, filters);
      onUpdate && onUpdate();
    } catch (error) {
      message.error("Failed to delete flow");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingFlow(null);
    form.resetFields();
    // Pre-fill with budget item's sub-account if available
    if (budgetItem?.subAccount?.id) {
      form.setFieldsValue({
        sourceAccountId: budgetItem.subAccount.id
      });
    }
    setShowModal(true);
  };

  const handleTableChange = (paginationConfig) => {
    loadFlows(paginationConfig.current, paginationConfig.pageSize, filters);
  };

  const handleFilter = async (values) => {
    const appliedFilters = {};

    Object.keys(values).forEach(key => {
      if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
        if (key === 'dateRange' && values[key]) {
          appliedFilters.startDate = values[key][0]?.format('YYYY-MM-DD');
          appliedFilters.endDate = values[key][1]?.format('YYYY-MM-DD');
        } else if (key !== 'dateRange') {
          appliedFilters[key] = values[key];
        }
      }
    });

    setFilters(appliedFilters);
  };

  const handleResetFilters = () => {
    filterForm.resetFields();
    setFilters({});
  };

  const columns = [
    {
      title: 'S/N',
      key: 'index',
      render: (_, __, index) => index + 1,
      width: 60
    },
    {
      title: 'Date',
      dataIndex: 'dateAsString',
      key: 'date'
    },
    {
      title: 'Flow Type',
      dataIndex: 'flowType',
      key: 'flowType',
      render: (flowType) => (
        <span style={{
          color: flowType === 'INFLOW' ? '#52c41a' : '#ff4d4f',
          fontWeight: 'bold'
        }}>
          {flowType}
        </span>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `${NAIRA} ${formatCurrency(amount)}`,
      align: 'right'
    },
    {
      title: 'Source Account',
      dataIndex: ['sourceAccount', 'title'],
      key: 'sourceAccount'
    },
    {
      title: 'Destination Account',
      dataIndex: ['destinationAccount', 'title'],
      key: 'destinationAccount'
    },
    {
      title: 'Identifier',
      dataIndex: 'identifier',
      key: 'identifier'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, flow) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(flow)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Flow"
            description="Are you sure you want to delete this flow?"
            onConfirm={() => handleDelete(flow)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5}>Inflow/Outflow Management</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
        >
          Add Flow
        </Button>
      </div>

      <Spin spinning={loading}>
        {/* Filter Section */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form
            form={filterForm}
            layout="vertical"
            onFinish={handleFilter}
          >
            <Row gutter={[16, 16]}>
              <Col span={4}>
                <Form.Item name="flowType" label="Flow Type">
                  <Select
                    placeholder="Select flow type"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Option value="INFLOW">INFLOW</Option>
                    <Option value="OUTFLOW">OUTFLOW</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item name="sourceAccountId" label="Source Account">
                  <Select
                    placeholder="Select source account"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {subAccounts.map(account => (
                      <Option key={account.id} value={account.id}>
                        {account.title} ({account.value})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item name="destinationAccountId" label="Destination Account">
                  <Select
                    placeholder="Select destination account"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {subAccounts.map(account => (
                      <Option key={account.id} value={account.id}>
                        {account.title} ({account.value})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item name="identifier" label="Identifier">
                  <Input placeholder="Search by identifier" allowClear />
                </Form.Item>
              </Col>
              {/*<Col span={6}>*/}
              {/*  <Form.Item name="identifier" label="Identifier">*/}
              {/*    <Input placeholder="Search by identifier" allowClear />*/}
              {/*  </Form.Item>*/}
              {/*</Col>*/}
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item name="dateRange" label="Date Range">
                  <DatePicker.RangePicker
                    format="YYYY-MM-DD"
                    style={{ width: '100%' }}
                    size="small"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="minAmount" label="Min Amount">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Min"
                    prefix={NAIRA}
                    size="small"
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="maxAmount" label="Max Amount">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Max"
                    prefix={NAIRA}
                    size="small"
                  />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item label=" ">
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SearchOutlined />} size="small">
                      Filter
                    </Button>
                    <Button onClick={handleResetFilters} icon={<ReloadOutlined />} size="small">
                      Reset
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Table
          columns={columns}
          dataSource={flows}
          rowKey="id"
          size="small"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: "No flows found"
          }}
        />
      </Spin>

      <Modal
        title={editingFlow ? "Edit Flow" : "Create New Flow"}
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          setEditingFlow(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="sourceAccountId"
            label="Source Account"
            rules={[{ required: true, message: 'Please select source account' }]}
          >
            <Select
              placeholder="Select source account"
              size="large"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {subAccounts.map(account => (
                <Option key={account.id} value={account.id}>
                  {account.title} ({account.value})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="destinationAccountId"
            label="Destination Account"
            rules={[{ required: true, message: 'Please select destination account' }]}
          >
            <Select
              placeholder="Select destination account"
              size="large"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {subAccounts.map(account => (
                <Option key={account.id} value={account.id}>
                  {account.title} ({account.value})
                </Option>
              ))}
            </Select>
          </Form.Item>


          <Form.Item
            name="flowType"
            label="Flow Type"
            rules={[{ required: true, message: 'Please select flow type' }]}
          >
            <Select
              placeholder="Select flow type"
              size="large"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option value="INFLOW">INFLOW</Option>
              <Option value="OUTFLOW">OUTFLOW</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount"
            rules={[
              { required: true, message: 'Please enter amount' },
              { type: 'number', min: 0.01, message: 'Amount must be greater than 0' }
            ]}
          >
            <InputNumber
              prefix={NAIRA}
              style={{ width: '100%' }}
              size="large"
              placeholder="Enter amount"
              precision={2}
            />
          </Form.Item>

          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Please select date' }]}
          >
            <DatePicker
              format={dateFormat}
              style={{ width: '100%' }}
              size="large"
              placeholder="Select date"
            />
          </Form.Item>

          <Form.Item
            name="identifier"
            label="Identifier (Optional)"
          >
            <Input
              placeholder="Enter identifier (e.g., PV-2025-001)"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter description"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => {
                setShowModal(false);
                setEditingFlow(null);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                {editingFlow ? 'Update' : 'Create'} Flow
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InflowOutflowManager;
